import React from 'react';
import { Users, TrendingUp, DollarSign, Mail, ArrowUp, ArrowDown, CheckCircle2 } from 'lucide-react';

const QuickStats = ({ leads, analytics, isDark }) => {
  const totalLeads = leads.length;
  const newLeads = leads.filter(l => l.status === 'new').length;
  // NaN-safe: some legacy leads may be missing intent_score entirely, and
  // Math.round(NaN) is NaN which React then renders as the string "NaN".
  const avgIntentScore = leads.length > 0
    ? Math.round(
        leads.reduce((sum, l) => sum + (Number.isFinite(l.intent_score) ? l.intent_score : 0), 0) /
        leads.length
      )
    : 0;
  const qualifiedLeads = leads.filter(l => l.status === 'qualified').length;
  const closedLeads = leads.filter(l => l.status === 'closed').length;

  const stats = [
    {
      id: 'total',
      label: 'Total Leads',
      value: totalLeads,
      icon: Users,
      ringGlow: 'from-cyan-500/15 to-cyan-500/0',
      iconText: 'text-cyan-300',
      valueText: 'text-white',
    },
    {
      id: 'new',
      label: 'New Leads',
      value: newLeads,
      icon: Mail,
      ringGlow: 'from-emerald-500/15 to-emerald-500/0',
      iconText: 'text-emerald-300',
      valueText: 'text-emerald-300',
      highlight: newLeads > 0,
    },
    {
      id: 'intent',
      label: 'Avg. Intent',
      value: avgIntentScore,
      suffix: '/100',
      icon: TrendingUp,
      ringGlow: 'from-amber-500/15 to-amber-500/0',
      iconText: 'text-amber-300',
      valueText: 'text-amber-300',
    },
    // Qualified Leads is a COUNT (integer). Non-monetary → CheckCircle icon,
    // not DollarSign. Only pair a $ icon with a currency value.
    {
      id: 'qualified',
      label: 'Qualified Leads',
      value: qualifiedLeads,
      icon: CheckCircle2,
      ringGlow: 'from-emerald-500/15 to-emerald-500/0',
      iconText: 'text-emerald-300',
      valueText: 'text-white',
    },
    {
      id: 'closed',
      label: 'Closed Deals',
      value: closedLeads,
      icon: DollarSign,
      ringGlow: 'from-violet-500/15 to-violet-500/0',
      iconText: 'text-violet-300',
      valueText: 'text-white',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="quick-stats">
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <div
            key={stat.id}
            className={`relative rounded-2xl p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-white/20 border border-white/10 bg-gradient-to-br ${stat.ringGlow} backdrop-blur-sm overflow-hidden`}
            data-testid={`stat-${stat.id}`}
          >
            <div className="relative flex items-start justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1 text-slate-400">
                  {stat.label}
                </p>
                <div className="flex items-baseline gap-1">
                  <span className={`text-2xl font-extrabold ${stat.valueText}`}>
                    {stat.value}
                  </span>
                  {stat.suffix && (
                    <span className="text-sm text-slate-500">
                      {stat.suffix}
                    </span>
                  )}
                </div>
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/5 border border-white/10">
                <Icon size={20} className={stat.iconText} />
              </div>
            </div>
            {stat.highlight && (
              <div className="relative mt-2 flex items-center gap-1">
                <ArrowUp size={12} className="text-emerald-400" />
                <span className="text-xs text-emerald-400 font-medium">New activity</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default QuickStats;
